'use client';

import { GoogleTagManager } from '@next/third-parties/google';
import { usePathname } from 'next/navigation';

const GTM_ID = 'GTM-P3DJW7RT';

/**
 * Environments that must never load GTM.
 */
const GTM_DISABLED_ENVIRONMENTS = ['test'];

/**
 * Routes that must not load GTM, each matching itself and anything nested under
 * it. Only decides what a page load starts with — GTM cannot be unloaded, so a
 * client-side navigation in from a tracked page still has it in memory.
 */
const GTM_EXCLUDED_ROUTES = ['/workspace-landing'];

const isExcluded = (pathname: string, appEnv: string) =>
  GTM_DISABLED_ENVIRONMENTS.includes(appEnv) ||
  GTM_EXCLUDED_ROUTES.some(
    (route) => pathname === route || pathname.startsWith(`${route}/`)
  );

interface Props {
  appEnv: string;
}

export function GoogleTagManagerScript({ appEnv }: Props) {
  const pathname = usePathname();

  if (isExcluded(pathname, appEnv)) return null;

  return <GoogleTagManager gtmId={GTM_ID} />;
}

/**
 * Fallback for browsers with JavaScript disabled. Render first inside <body>.
 */
export function GoogleTagManagerNoScript({ appEnv }: Props) {
  const pathname = usePathname();

  if (isExcluded(pathname, appEnv)) return null;

  return (
    <noscript>
      <iframe
        src={`https://www.googletagmanager.com/ns.html?id=${GTM_ID}`}
        height="0"
        width="0"
        style={{ display: 'none', visibility: 'hidden' }}
      />
    </noscript>
  );
}
